import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  Text,
  Input,
  VStack,
  HStack,
  Avatar,
  useColorModeValue,
  Flex,
  Spinner,
  InputGroup,
  InputRightElement,
  useToast,
  Icon,
  Center,
  useBreakpointValue,
  Tooltip,
  Badge,
  Divider,
  ScaleFade,
  SlideFade,
  Textarea
} from '@chakra-ui/react';
import {
  Robot,
  PaperPlaneTilt,
  ChatCircleText,
  X,
  ChatCircleDots,
  Broom,
  Sparkle,
  Lightning,
  ArrowUp,
  Minus
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import aiService from '../../services/aiService';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MAX_MESSAGE_LENGTH = 500;

const MessageBubble = ({ content, type, sources, timestamp, isLoading }) => {
  const isUser = type === 'user';
  const isError = content.startsWith('Error:');
  
  // Enhanced color scheme matching Explorer's Journal theme
  const userBg = useColorModeValue('linear-gradient(135deg, interactive.primary 0%, accent.gold.600 100%)', 'linear-gradient(135deg, interactive.primary 0%, accent.gold.600 100%)');
  const botBg = useColorModeValue('bg.surface', 'bg.surface');
  const errorBg = useColorModeValue('error.50', 'error.900');
  const textColor = useColorModeValue('text.default', 'text.default');
  const userTextColor = useColorModeValue('white', 'white');
  const mutedColor = useColorModeValue('text.muted', 'text.muted');
  const headerColor = useColorModeValue('interactive.primary', 'interactive.primary');

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      alignSelf={isUser ? 'flex-end' : 'flex-start'}
      maxWidth={{ base: "90%", md: "85%" }}
      mb={2}
    >
      {isUser ? (
        <MotionBox
          bg={userBg}
          color={userTextColor}
          px={5}
          py={4}
          borderRadius="2xl"
          borderBottomRightRadius="lg"
          boxShadow="0 4px 16px rgba(193, 148, 52, 0.2)"
          border="1px solid"
          borderColor="whiteAlpha.300"
          position="relative"
          _before={{
            content: '""',
            position: 'absolute',
            bottom: 0,
            right: -2,
            width: 0,
            height: 0,
            borderLeft: '10px solid',
            borderLeftColor: 'interactive.primary',
            borderBottom: '10px solid transparent'
          }}
          whileHover={{ 
            scale: 1.02,
            boxShadow: "0 6px 20px rgba(193, 148, 52, 0.3)"
          }}
          transition={{ duration: 0.2 }}
        >
          <Text fontSize="sm" fontFamily="body" lineHeight="1.5">{content}</Text>
        </MotionBox>
      ) : (
        <VStack align="start" spacing={2} width="100%">
          <MotionFlex
            bg={isError ? errorBg : botBg}
            px={5}
            py={4}
            borderRadius="2xl"
            borderBottomLeftRadius="lg"
            borderWidth="2px"
            borderColor={isError ? 'error' : 'border.light'}
            boxShadow="0 4px 16px rgba(59, 46, 30, 0.08)"
            position="relative"
            direction="column"
            width="100%"
            _before={{
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: -2,
              width: 0,
              height: 0,
              borderRight: '10px solid',
              borderRightColor: isError ? errorBg : botBg,
              borderBottom: '10px solid transparent'
            }}
            whileHover={{ 
              scale: 1.01,
              boxShadow: "0 6px 20px rgba(59, 46, 30, 0.12)"
            }}
            transition={{ duration: 0.2 }}
          >
            <HStack mb={3} spacing={3}>
              <Avatar 
                size="sm" 
                bg={headerColor} 
                icon={<Robot size={16} weight="fill" color="white" />}
                boxShadow="0 2px 8px rgba(193, 148, 52, 0.2)"
                border="2px solid"
                borderColor="border.light"
              />
              <Text fontSize="sm" fontWeight="bold" color={textColor} fontFamily="heading" letterSpacing="wide">
                Site Assistant
              </Text>
              {!isError && !isLoading && (
                <Badge
                  bg="interactive.primary"
                  color="white"
                  variant="solid"
                  borderRadius="full"
                  fontSize="xs"
                  px={2}
                  py={0.5}
                  fontFamily="heading"
                  fontWeight="semibold"
                  letterSpacing="wider"
                >
                  <Icon as={Sparkle} boxSize={2.5} mr={1} />
                  AI
                </Badge>
              )}
            </HStack>
            
            <Text 
              fontSize="sm" 
              color={isError ? 'red.600' : textColor} 
              fontFamily="body" 
              lineHeight="1.6"
              whiteSpace="pre-wrap"
            >
              {content}
            </Text>
            
            {sources && sources.length > 0 && (
              <VStack align="start" mt={3} spacing={2} width="100%">
                <Divider />
                <Text fontSize="xs" color={mutedColor} fontWeight="semibold" fontFamily="heading">
                  📚 Sources:
                </Text>
                <VStack align="start" spacing={1} width="100%">
                  {sources.map((source, idx) => (
                    <Button
                      key={idx}
                      variant="ghost"
                      size="xs"
                      color="interactive.primary"
                      fontFamily="body"
                      justifyContent="flex-start"
                      height="auto"
                      py={1}
                      px={2}
                      borderRadius="md"
                      _hover={{
                        bg: useColorModeValue('interactive.primary', 'interactive.primary') + '20',
                        transform: 'translateX(4px)'
                      }}
                      transition="all 0.2s"
                      onClick={() => {
                        const url = source.url || source.web?.uri;
                        if (url && url !== '#') window.open(url, '_blank');
                      }}
                    >
                      <Text fontSize="xs" noOfLines={2}>
                        {source.title || `Source ${idx + 1}`}
                      </Text>
                    </Button>
                  ))}
                </VStack>
              </VStack>
            )}
          </MotionFlex>
        </VStack>
      )}
      
      <Text 
        fontSize="xs" 
        color={mutedColor} 
        mt={1} 
        textAlign={isUser ? 'right' : 'left'}
        fontFamily="body"
      >
        {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Text>
    </MotionBox>
  );
};

const ChatBot = ({ site, isAuthenticated, isVisible: propIsVisible = true, onToggleVisibility: propOnToggleVisibility, className }) => {
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(`chat_history_${site.id}`);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error('Error loading chat history:', e);
      return [];
    }
  });

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentIsVisible, setCurrentIsVisible] = useState(propIsVisible); // Renamed to avoid conflict
  const [isMinimized, setIsMinimized] = useState(true); // For desktop: panel open or FAB - start minimized

  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const [isPanelOpen, setIsPanelOpen] = useState(false); // For mobile: panel open or FAB - start minimized

  const messagesEndRef = useRef(null);
  const toast = useToast();
  const navigate = useNavigate();

  const bgColor = useColorModeValue('bg.default', 'bg.default'); // Changed
  // const borderColor = useColorModeValue('gray.200', 'gray.700'); // No longer needed for panel, using semantic
  const headerColor = useColorModeValue('bg.accent', 'bg.accent');
  const textColor = useColorModeValue('text.default', 'text.default'); // Added for intro message
  const mutedColor = useColorModeValue('text.muted', 'text.muted'); // Added for intro message

  useEffect(() => {
    localStorage.setItem(`chat_history_${site.id}`, JSON.stringify(messages));
    if (messagesEndRef.current) { // Scroll if panel is open and messages exist
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, site.id]);
  
  useEffect(() => {
    setCurrentIsVisible(propIsVisible);
  }, [propIsVisible]);

  const validateQuestion = (question) => {
    const irrelevantKeywords = [
      'politics', 'election', 'government', 'vote',
      'stock', 'market', 'price', 'buy', 'sell',
      'weather', 'today', 'tomorrow',
      'personal', 'advice', 'help me',
      'opinion on', 'think about'
    ];
    const containsIrrelevantTopic = irrelevantKeywords.some(keyword =>
      question.toLowerCase().includes(keyword)
    );
    return !containsIrrelevantTopic;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim()) return;
    if (input.length > MAX_MESSAGE_LENGTH) {
        toast({
            title: 'Message too long',
            description: `Please keep your message under ${MAX_MESSAGE_LENGTH} characters.`,
            status: 'warning',
            duration: 3000,
            isClosable: true,
        });
        return;
    }

    const userMessageText = input;

    if (!validateQuestion(userMessageText)) {
      toast({
        title: 'Off-topic Question',
        description: `Please ask questions related to ${site.name}, its history, or archaeological context.`,
        status: 'warning',
        duration: 5000,
        isClosable: true
      });
      return;
    }

    setInput('');
    const userMessage = {
      content: userMessageText,
      type: 'user',
      timestamp: new Date().toISOString()
    };
    
    const botThinkingMessageId = Date.now() + '_thinking';
    setMessages(prev => [
        ...prev, 
        userMessage,
        { 
            id: botThinkingMessageId,
            content: 'Thinking...', 
            type: 'bot', 
            isLoading: true,
            timestamp: new Date().toISOString() 
        }
    ]);
    setLoading(true);

    try {
      const apiConversationHistory = messages.map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content,
      }));
      apiConversationHistory.push({ role: 'user', content: userMessageText });

      const response = await aiService.processChatQuery(
        userMessageText,
        site,
        {}, 
        apiConversationHistory
      );
      
      setMessages(prev => prev.map(msg =>
        msg.id === botThinkingMessageId
        ? {
            content: response.data?.reply || 'Sorry, I couldn\'t get a response.',
            type: 'bot',
            sources: response.data?.sources,
            timestamp: new Date().toISOString(),
            isLoading: false
          }
        : msg
      ));

    } catch (error) {
      console.error('Error in chat query:', error);
      setMessages(prev => prev.map(msg => 
        msg.id === botThinkingMessageId
        ? {
            content: `Error: ${error.message || 'Failed to get response.'}`,
            type: 'bot',
            timestamp: new Date().toISOString(),
            isError: true,
            isLoading: false
          }
        : msg
      ));
      toast({
        title: 'Error',
        description: error.message || 'Failed to process your question',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setLoading(false);
    }
  };

  const introMessage = `I can help you learn more about ${site.name}. Ask me about its history, artifacts, or cultural significance.`;

  if (!isAuthenticated) {
    return null;
  }
  if (!currentIsVisible) {
    return null;
  }
  if (!site || !site.id || !site.name) {
    return null;
  }

  const showFabCondition = (isMobile && !isPanelOpen) || (!isMobile && isMinimized);
  const hasUnreadMessages = messages.length > 0;


  const fabJsx = (
    <Box
      key={`site-chatbot-fab-${site.id}`}
      position="fixed"
      bottom="24px"
      right="24px"
      zIndex={10000}
      role="group"
      data-component="site-profile-chatbot"
    >
      <Box
        as="button"
        width="72px"
        height="72px"
        borderRadius="full"
        bg="linear-gradient(135deg, interactive.primary 0%, accent.gold.600 100%)"
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="0 12px 40px rgba(193, 148, 52, 0.3)"
        border="3px solid"
        borderColor="bg.surface"
        cursor="pointer"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{
          transform: "scale(1.05) translateY(-3px)",
          boxShadow: "0 16px 48px rgba(193, 148, 52, 0.4)",
          borderColor: "accent.gold.300"
        }}
        _active={{
          transform: "scale(0.98)"
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (isMobile) {
            setIsPanelOpen(true);
          } else {
            setIsMinimized(false);
          }
        }}
        aria-label="Open Site Assistant"
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bg: "radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, transparent 70%)",
          borderRadius: "full",
          opacity: 0.8
        }}
      >
        <Icon
          as={Robot}
          boxSize={8}
          weight="fill"
          color="white"
          filter="drop-shadow(0 2px 4px rgba(0,0,0,0.2))"
          zIndex={1}
        />
        
        {/* Elegant pulse ring */}
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width="100%"
          height="100%"
          borderRadius="full"
          border="2px solid"
          borderColor="interactive.primary"
          opacity={0.4}
          animation="elegantPulse 3s infinite"
          sx={{
            '@keyframes elegantPulse': {
              '0%': {
                transform: 'translate(-50%, -50%) scale(1)',
                opacity: 0.4,
              },
              '50%': {
                transform: 'translate(-50%, -50%) scale(1.2)',
                opacity: 0.1,
              },
              '100%': {
                transform: 'translate(-50%, -50%) scale(1)',
                opacity: 0.4,
              },
            },
          }}
        />
        
        {/* Message indicator */}
        {hasUnreadMessages && (
          <Box
            position="absolute"
            top="-2px"
            right="-2px"
            width="24px"
            height="24px"
            borderRadius="full"
            bg="error"
            color="white"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="xs"
            fontWeight="bold"
            fontFamily="heading"
            border="3px solid"
            borderColor="bg.surface"
            boxShadow="0 2px 8px rgba(0,0,0,0.2)"
          >
            !
          </Box>
        )}
        
        {/* Floating label */}
        <Box
          position="absolute"
          bottom="-12px"
          left="50%"
          transform="translateX(-50%)"
          bg="bg.surface"
          color="text.default"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="xs"
          fontFamily="heading"
          fontWeight="semibold"
          border="1px solid"
          borderColor="border.light"
          boxShadow="0 4px 12px rgba(0,0,0,0.1)"
          opacity={0}
          transition="all 0.3s ease"
          _groupHover={{ opacity: 1, transform: "translateX(-50%) translateY(-4px)" }}
          whiteSpace="nowrap"
        >
          Site Assistant
        </Box>
      </Box>
    </Box>
  );

  const panelJsx = (
    <Box
      key={`site-chatbot-panel-${site.id}`}
      position="fixed"
      bottom={isMobile ? 0 : 6}
      right={isMobile ? 0 : 6}
      width={isMobile ? "100%" : "400px"}
      height={isMobile ? "100%" : "auto"}
      maxHeight={isMobile ? "100vh" : "80vh"}
      zIndex={1000}
      display="flex"
      flexDirection="column"
      data-component="site-profile-chatbot"
    >
      {/* Mobile backdrop */}
      {isMobile && (
        <MotionBox
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.600"
          backdropFilter="blur(4px)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => setIsPanelOpen(false)}
        />
      )}
      
      <MotionBox
        initial={{ 
          opacity: 0, 
          y: isMobile ? "100%" : 30,
          scale: isMobile ? 1 : 0.9
        }}
        animate={{ 
          opacity: 1, 
          y: 0,
          scale: 1
        }}
        exit={{ 
          opacity: 0, 
          y: isMobile ? "100%" : 30,
          scale: isMobile ? 1 : 0.9
        }}
        transition={{ 
          duration: 0.4, 
          ease: "easeOut",
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
        bg={bgColor}
        borderRadius={{ base: isMobile ? "24px 24px 0 0" : "2xl", md: "2xl" }}
        boxShadow={isMobile ? "0 -12px 48px rgba(59, 46, 30, 0.2)" : "0 24px 64px rgba(59, 46, 30, 0.15)"}
        borderWidth="2px"
        borderColor="border.light"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        position="relative"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: useColorModeValue(0.05, 0.1),
          pointerEvents: 'none',
          bgImage: useColorModeValue(
            "radial-gradient(circle at 30% 20%, rgba(193, 148, 52, 0.08) 0%, transparent 60%)",
            "radial-gradient(circle at 30% 20%, rgba(214, 173, 96, 0.06) 0%, transparent 60%)"
          ),
          zIndex: 0
        }}
      >
            {/* Header */}
            <Flex
              px={6}
              py={4}
              bg="linear-gradient(135deg, interactive.primary 0%, accent.gold.600 100%)"
              color="white"
              alignItems="center"
              justifyContent="space-between"
              flexShrink={0}
              position="relative"
              borderBottom="1px solid"
              borderColor="whiteAlpha.200"
              _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgImage: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)",
                pointerEvents: 'none'
              }}
              _after={{
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '2px',
                bg: 'linear-gradient(90deg, transparent 0%, whiteAlpha.400 50%, transparent 100%)'
              }}
            >
              {/* Mobile drag handle */}
              {isMobile && (
                <Box
                  position="absolute"
                  top={2}
                  left="50%"
                  transform="translateX(-50%)"
                  width={10}
                  height={1}
                  bg="whiteAlpha.600"
                  borderRadius="full"
                />
              )}
              
              <HStack spacing={4}>
                <Avatar
                  size="md"
                  icon={<Robot size={24} weight="fill" />}
                  bg="whiteAlpha.300"
                  color="white"
                  boxShadow="0 4px 12px rgba(0,0,0,0.2)"
                  border="2px solid"
                  borderColor="whiteAlpha.300"
                />
                <Box>
                  <HStack spacing={3} align="center">
                    <Text fontWeight="bold" fontSize="lg" fontFamily="heading" letterSpacing="wide">
                      Site Assistant
                    </Text>
                    <Badge
                      bg="whiteAlpha.300"
                      color="white"
                      size="sm"
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontFamily="heading"
                      fontWeight="semibold"
                      fontSize="xs"
                      letterSpacing="wider"
                      border="1px solid"
                      borderColor="whiteAlpha.400"
                    >
                      <Icon as={Sparkle} boxSize={3} mr={1} />
                      AI
                    </Badge>
                  </HStack>
                  <Text fontSize="sm" opacity={0.95} fontFamily="body" noOfLines={1} mt={1}>
                    Ask about {site.name}
                  </Text>
                </Box>
              </HStack>
              
              <HStack spacing={1}>
                {!isMobile && (
                  <Tooltip label="Minimize" placement="bottom">
                    <IconButton
                      icon={<Minus size={16} />}
                      size="sm"
                      variant="ghost"
                      color="white"
                      _hover={{ bg: 'whiteAlpha.200' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMinimized(true);
                      }}
                      aria-label="Minimize Assistant"
                    />
                  </Tooltip>
                )}
                
                <Tooltip label="Clear chat" placement="bottom">
                  <IconButton
                    icon={<Broom size={16} />}
                    size="sm"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      localStorage.removeItem(`chat_history_${site.id}`);
                      setMessages([]);
                      toast({
                        title: "Chat cleared",
                        status: "info",
                        duration: 2000,
                        isClosable: true,
                      });
                    }}
                    aria-label="Clear chat"
                  />
                </Tooltip>
                
                <Tooltip label="Close Assistant" placement="bottom">
                  <IconButton
                    icon={<X size={16} />}
                    size="sm"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isMobile) {
                        setIsPanelOpen(false);
                      } else {
                        setIsMinimized(true);
                      }
                    }}
                    aria-label="Close Assistant"
                  />
                </Tooltip>
              </HStack>
            </Flex>

            {/* Chat content */}
            <VStack spacing={0} align="stretch" flexGrow={1} overflow="hidden">
                <Box
                  px={4}
                  py={4}
                  flexGrow={1}
                  overflowY="auto"
                  bg="bg.default"
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: useColorModeValue('#f1f1f1', '#2d3748'),
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: useColorModeValue('#c1c1c1', '#4a5568'),
                      borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: useColorModeValue('#a8a8a8', '#2d3748'),
                    },
                  }}
                >
                  {messages.length === 0 && !loading ? (
                    <Center py={12} flexDirection="column">
                      <MotionBox
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                      >
                        <Icon
                          as={ChatCircleText}
                          boxSize={16}
                          color={mutedColor}
                          mb={6}
                        />
                      </MotionBox>
                      
                      <VStack spacing={4} textAlign="center" maxW="280px">
                        <Text
                          fontSize="lg"
                          fontWeight="semibold"
                          color={textColor}
                          fontFamily="heading"
                        >
                          Welcome to Site Assistant
                        </Text>
                        
                        <Text
                          fontSize="sm"
                          color={mutedColor}
                          lineHeight="1.6"
                          fontFamily="body"
                        >
                          {introMessage}
                        </Text>
                        
                        <VStack spacing={2} mt={4} width="100%">
                          <Text fontSize="xs" color={mutedColor} fontWeight="semibold">
                            Try asking:
                          </Text>
                          {[
                            "What is the history of this site?",
                            "Tell me about the artifacts found here",
                            "What makes this site significant?"
                          ].map((suggestion, idx) => (
                            <Button
                              key={idx}
                              size="sm"
                              variant="ghost"
                              color={mutedColor}
                              fontSize="xs"
                              height="auto"
                              py={2}
                              px={3}
                              borderRadius="lg"
                              _hover={{
                                bg: useColorModeValue('bg.surface', 'bg.surface'),
                                color: textColor
                              }}
                              onClick={() => setInput(suggestion)}
                            >
                              "{suggestion}"
                            </Button>
                          ))}
                        </VStack>
                      </VStack>
                    </Center>
                  ) : (
                    <VStack spacing={3} align="stretch" pb={4}>
                      {messages.map((msg, idx) => (
                        msg.isLoading 
                        ? (
                            <MotionBox
                              key={msg.id || idx}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              alignSelf="flex-start"
                              maxWidth="85%"
                            >
                              <HStack
                                bg={useColorModeValue('bg.surface', 'bg.surface')}
                                px={4}
                                py={3}
                                borderRadius="2xl"
                                borderBottomLeftRadius="md"
                                spacing={3}
                              >
                                <Avatar 
                                  size="xs" 
                                  bg={headerColor} 
                                  icon={<Robot size={12} weight="fill" color="white"/>}
                                />
                                <HStack spacing={2}>
                                  <Spinner size="sm" color="interactive.primary" />
                                  <Text fontSize="sm" color={textColor}>
                                    Thinking...
                                  </Text>
                                </HStack>
                              </HStack>
                            </MotionBox>
                          )
                        : <MessageBubble key={msg.id || idx} {...msg} isLoading={msg.isLoading} />
                      ))}
                      <Box ref={messagesEndRef} />
                    </VStack>
                  )}
                </Box>

                {/* Input area */}
                <Box 
                  p={6} 
                  borderTopWidth="2px" 
                  borderColor="border.light" 
                  flexShrink={0}
                  bg={useColorModeValue('bg.accent', 'bg.accent')}
                  position="relative"
                  _before={{
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    bg: 'linear-gradient(90deg, transparent 0%, border.default 50%, transparent 100%)'
                  }}
                >
                  <form onSubmit={handleSubmit}>
                    <VStack spacing={3}>
                      <InputGroup size="lg">
                        <Textarea
                          placeholder="Ask me anything about this site..."
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          resize="none"
                          rows={isMobile ? 2 : 1}
                          pr="5rem"
                          borderRadius="2xl"
                          borderWidth="2px"
                          borderColor="border.light"
                          bg="bg.surface"
                          isDisabled={loading}
                          fontFamily="body"
                          fontSize="md"
                          color="text.default"
                          _placeholder={{
                            color: "text.muted",
                            fontStyle: "italic"
                          }}
                          _focus={{
                            borderColor: "interactive.primary",
                            boxShadow: "0 0 0 3px rgba(193, 148, 52, 0.1)",
                            bg: "bg.default"
                          }}
                          _hover={{
                            borderColor: "interactive.hover"
                          }}
                          transition="all 0.2s ease"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSubmit();
                            }
                          }}
                        />
                        <InputRightElement height="100%" width="5rem">
                          <IconButton
                            icon={loading ? <Spinner size="sm" color="white" /> : <ArrowUp size={20} weight="bold" />}
                            size="md"
                            isRound
                            bg="linear-gradient(135deg, interactive.primary 0%, accent.gold.600 100%)"
                            color="white"
                            type="submit"
                            isDisabled={!input.trim() || loading}
                            border="2px solid"
                            borderColor="whiteAlpha.300"
                            boxShadow="0 4px 12px rgba(193, 148, 52, 0.3)"
                            _hover={{
                              transform: "scale(1.08) translateY(-1px)",
                              boxShadow: "0 6px 16px rgba(193, 148, 52, 0.4)",
                              borderColor: "whiteAlpha.500"
                            }}
                            _active={{
                              transform: "scale(0.96)"
                            }}
                            _disabled={{
                              opacity: 0.6,
                              cursor: "not-allowed",
                              transform: "none",
                              boxShadow: "none"
                            }}
                            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                            aria-label="Send message"
                          />
                        </InputRightElement>
                      </InputGroup>
                      
                      <HStack justify="space-between" width="100%" fontSize="xs">
                        <Text color={mutedColor}>
                          Press Enter to send, Shift+Enter for new line
                        </Text>
                        <Text
                          color={
                            (MAX_MESSAGE_LENGTH - input.length) < 50
                              ? useColorModeValue("red.500", "red.300")
                              : mutedColor
                          }
                        >
                          {MAX_MESSAGE_LENGTH - input.length} left
                        </Text>
                      </HStack>
                    </VStack>
                  </form>
                </Box>
            </VStack>
          </MotionBox>
    </Box>
  );

  return (
    <AnimatePresence mode="wait" initial={false}>
      {showFabCondition ? (
        <Box key="fab" className={className}>{fabJsx}</Box>
      ) : (
        <Box key="panel" className={className}>{panelJsx}</Box>
      )}
    </AnimatePresence>
  );
};

ChatBot.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool,
  onToggleVisibility: PropTypes.func,
  className: PropTypes.string,
};

export default ChatBot;
